import { isDebugContext } from './env.ts';

// https://stackoverflow.com/questions/41102060/typescript-extending-error-class
export class CustomError extends Error {
  constructor(
    message?: string,
    readonly args?: Record<string, unknown>,
  ) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// This is thrown by die() and is not intended to be caught.
// Other error types that are not intended to be caught may subclass this.
export class UncatchableError extends CustomError {}

// If non-test code catches `CheckError` and tests the content of the message string, you should
// instead throw a more specific `CustomError` in the `DeferredMessage` callback. Example:
//   checkPresent(value, () => throw new MyCustomError('message'));
// Typically CheckErrors are unexpected and are therefore not caught and examined. In such cases,
// it is simpler and better to just use the DeferredMessage callback to provide a descriptive
// string, rather than creating a new CustomError type.
export class CheckError extends UncatchableError {}

// DNS: Remove | string... just for transition.
export type DeferredMessage = (() => string) | string;

export function resolveDeferredMessages(...messages: DeferredMessage[]) {
  // TODO: Conduct performance benchmark of DeferredMessage (requiring closure) vs string
  // interpolation.
  return messages.map((m) => (typeof m === 'string' ? m : m()));
}

// TODO: This file arrangement sucks. Think about how to avoid circularity.
export function die(
  message: string | DeferredMessage,
  ...messages: (string | DeferredMessage)[]
): never {
  const resolvedMessages = resolveDeferredMessages(message, ...messages);
  if (isDebugContext()) {
    // TODO: Make this true if --debug is set (which is node-only right now)
    console.info('die with error:', ...resolvedMessages);
    // eslint-disable-next-line no-debugger
    debugger;
  }
  throw new UncatchableError(resolvedMessages.join(' '));
}

export function checkFail(
  message: string | DeferredMessage,
  ...messages: (string | DeferredMessage)[]
): never {
  const resolvedMessages = resolveDeferredMessages(message, ...messages);
  if (isDebugContext()) {
    // TODO: Make this true if --debug is set (which is node-only right now)
    console.info('die with error:', ...resolvedMessages);
    // eslint-disable-next-line no-debugger
    debugger;
  }
  throw new CheckError(resolvedMessages.join(' '));
}

// Dies if |expression| is false.
export function check(expression: boolean, ...messages: DeferredMessage[]): asserts expression {
  if (!expression) {
    checkFail('check failed.', ...messages);
  }
}

// Dies if |value| is undefined. Returns the narrowed type.
export function checkPresent<T>(value: T | undefined | null, ...messages: DeferredMessage[]): T {
  if (value === undefined || value === null) {
    checkFail('checkPresent failed.', ...messages);
  }
  return value;
}

export function assertPresent<T>(
  value: T | undefined | null,
  ...messages: DeferredMessage[]
): asserts value is T {
  if (value === undefined || value === null) {
    checkFail('assertPresent failed.', ...messages);
  }
}

export function assertAbsent<T>(
  value: T | undefined | null,
  ...messages: DeferredMessage[]
): asserts value is undefined | null {
  if (value !== undefined && value !== null) {
    checkFail('assertAbsent failed.', ...messages);
  }
}

export const PresentCheck = Symbol('PresentCheck');

export function assertError(value: unknown): asserts value is Error {
  if (value instanceof Error) {
    if (value instanceof UncatchableError) {
      throw value;
    }
  } else {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    checkFail('assertError failed.', () => `${value}`);
  }
}

// https://www.meticulous.ai/blog/safer-exhaustive-switch-statements-in-typescript
export function assertNever(value: never): never {
  checkFail('assertNever reached.', () => `Unexpected value: ${value}`);
}

export class NotImplementedError extends CustomError {}

export function notImplemented(...messages: DeferredMessage[]): never {
  const resolvedMessages = resolveDeferredMessages('Not Implemented.', ...messages);
  if (isDebugContext()) {
    console.error('Not implemented', resolvedMessages);
  }
  throw new NotImplementedError(resolvedMessages.join(' '));
}
